/**
 * The documentation folder list as a picker shows it: every top-level
 * folder followed by its sub-folders and THEIR sub-folders (TWO levels —
 * 2026-10-31; one level since 2026-09-23), so an upload can be filed into
 * "Videos", "Videos / R11.0" or "Videos / R11.0 / Command Control" from one
 * select.
 *
 * A sub-folder whose parent is not in the list is treated as top-level
 * rather than dropped, matching the sidebar tree.
 */
export interface FolderLike {
  id: number;
  name: string;
  parent_id?: number | null;
}

/** How deep a folder may sit: 0 = folder, 1 = sub-folder, 2 = a sub-folder
 * of a sub-folder. portal-api's `DocumentCategory::MAX_DEPTH` is the
 * authority; this mirrors it so the UI never offers what it would refuse. */
export const MAX_FOLDER_DEPTH = 2;

export type FolderDepth = 0 | 1 | 2;

export interface FolderOption {
  id: number;
  name: string;
  depth: FolderDepth;
  /** "Parent / Child / Grandchild" — what a closed select displays. */
  label: string;
}

const byName = (a: FolderLike, b: FolderLike): number => a.name.localeCompare(b.name);

export function folderOptions(input: readonly FolderLike[]): FolderOption[] {
  // Tolerate a malformed response rather than blanking the form.
  const cats = Array.isArray(input) ? input : [];
  const ids = new Set(cats.map((c) => c.id));
  const isRoot = (c: FolderLike): boolean => c.parent_id == null || !ids.has(c.parent_id) || c.parent_id === c.id;
  const out: FolderOption[] = [];
  const seen = new Set<number>();
  const walk = (folder: FolderLike, depth: number, path: string): void => {
    // A legacy row deeper than the limit, or a cycle, is not listed twice.
    if (seen.has(folder.id)) return;
    seen.add(folder.id);
    out.push({ id: folder.id, name: folder.name, depth: Math.min(depth, MAX_FOLDER_DEPTH) as FolderDepth, label: path });
    for (const child of cats.filter((c) => !isRoot(c) && c.parent_id === folder.id).sort(byName)) {
      walk(child, depth + 1, `${path} / ${child.name}`);
    }
  };
  for (const root of cats.filter(isRoot).sort(byName)) walk(root, 0, root.name);
  return out;
}

/** Folders a NEW sub-folder may go under: any folder not already at the
 * deepest tier. */
export function parentFolderOptions(cats: readonly FolderLike[]): FolderOption[] {
  return folderOptions(cats).filter((o) => o.depth < MAX_FOLDER_DEPTH);
}
